/**
 * Baner zgód cookie (vanilla-cookieconsent v3) + Google Consent Mode v2.
 *
 * Kategorie:
 *  - necessary  - zawsze włączone (niezbędne do działania sklepu),
 *  - analytics  - Google Analytics (pomiar ruchu),
 *  - ads        - marketing / Google Ads (remarketing, konwersje).
 *
 * Współpraca z Google Tag Manager (wtyczka GTM4WP):
 *  1. Stan domyślny Consent Mode („denied") ustawia motyw w <head>, PRZED
 *     kontenerem GTM — patrz includes/general-functions.php
 *     (opal_output_consent_mode_default).
 *  2. Tagi GA4 / Google Ads są w kontenerze GTM i respektują Consent Mode.
 *  3. Po akceptacji/zmianie zgód ten plik odpala gtag('consent','update', …),
 *     mapując kategorie banera na sygnały Consent Mode.
 *
 * autoClear kasuje ciasteczka Google przy wycofaniu zgody na daną kategorię.
 */

import * as CookieConsent from 'vanilla-cookieconsent'

// gtag jest zdefiniowany w <head> (bootstrap Consent Mode). Fallback na wypadek,
// gdyby bootstrap nie zdążył — żeby nic nie wyrzuciło błędu.
function gtag() {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(arguments)
}

/**
 * Synchronizuje Google Consent Mode ze stanem zgód w banerze.
 * Wywoływane po pierwszej zgodzie, przy każdym ładowaniu strony (jeśli zgoda
 * już zapisana) oraz przy każdej zmianie preferencji.
 */
function syncConsentMode() {
  const analytics = CookieConsent.acceptedCategory('analytics')
  const ads = CookieConsent.acceptedCategory('ads')

  gtag('consent', 'update', {
    analytics_storage: analytics ? 'granted' : 'denied',
    ad_storage: ads ? 'granted' : 'denied',
    ad_user_data: ads ? 'granted' : 'denied',
    ad_personalization: ads ? 'granted' : 'denied',
    personalization_storage: ads ? 'granted' : 'denied',
    // Funkcjonalne traktujemy jak niezbędne – zawsze przyznane.
    functionality_storage: 'granted',
  })
}

export default function initCookieConsent() {
  CookieConsent.run({
    guiOptions: {
      consentModal: {
        layout: 'box',
        position: 'bottom right',
        flipButtons: false,
        equalWeightButtons: true,
      },
      preferencesModal: {
        layout: 'box',
        equalWeightButtons: true,
        flipButtons: false,
      },
    },

    // Consent Mode: przekazujemy stan zgód do Google.
    // Zwrócony użytkownik (zgoda już w cookie) dostaje „update" wcześnie w <head>
    // — patrz includes/general-functions.php. Dlatego NIE używamy onConsent
    // (odpala się przy każdym ładowaniu i dublowałby tamten update). Wystarczą:
    //  - onFirstConsent: pierwszy wybór (cookie nie istniało jeszcze w <head>),
    //  - onChange: zmiana preferencji.
    onFirstConsent: syncConsentMode,
    onChange: syncConsentMode,

    categories: {
      necessary: {
        enabled: true,
        readOnly: true,
      },
      analytics: {
        autoClear: {
          cookies: [
            { name: /^_ga/ },
            { name: '_gid' },
          ],
        },
      },
      ads: {
        autoClear: {
          cookies: [
            { name: /^_gcl/ },
          ],
        },
      },
    },

    language: {
      default: 'pl',
      translations: {
        pl: {
          consentModal: {
            title: 'Dbamy o Twoją prywatność',
            description:
              'Używamy plików cookie, aby zapewnić poprawne działanie sklepu oraz - za Twoją zgodą - analizować ruch i prowadzić działania marketingowe. Możesz zaakceptować wszystkie pliki cookie lub dostosować swoje preferencje. Więcej w <a href="/polityka-prywatnosci/">Polityce prywatności</a>.',
            acceptAllBtn: 'Akceptuję wszystkie',
            acceptNecessaryBtn: 'Tylko niezbędne',
            showPreferencesBtn: 'Ustawienia',
          },
          preferencesModal: {
            title: 'Ustawienia plików cookie',
            acceptAllBtn: 'Akceptuję wszystkie',
            acceptNecessaryBtn: 'Tylko niezbędne',
            savePreferencesBtn: 'Zapisz ustawienia',
            closeIconLabel: 'Zamknij',
            sections: [
              {
                title: 'Wykorzystanie plików cookie',
                description:
                  'Niezbędne pliki cookie umożliwiają podstawowe funkcje sklepu, takie jak koszyk czy realizacja zamówienia. Pliki analityczne i marketingowe pomagają nam zrozumieć, jak korzystasz ze strony, i docierać z ofertą.',
              },
              {
                title: 'Niezbędne pliki cookie',
                description:
                  'Wymagane do prawidłowego działania strony - nie można ich wyłączyć.',
                linkedCategory: 'necessary',
              },
              {
                title: 'Analityczne pliki cookie',
                description:
                  'Umożliwiają zbieranie anonimowych statystyk (Google Analytics), dzięki którym ulepszamy sklep.',
                linkedCategory: 'analytics',
              },
              {
                title: 'Marketingowe pliki cookie',
                description:
                  'Umożliwiają remarketing i pomiar skuteczności reklam (Google Ads).',
                linkedCategory: 'ads',
              },
              {
                title: 'Więcej informacji',
                description:
                  'Szczegóły znajdziesz w naszej <a href="/polityka-prywatnosci/">Polityce prywatności</a>. W razie pytań napisz na <a href="mailto:domator@marczak.pl">domator@marczak.pl</a>.',
              },
            ],
          },
        },
      },
    },
  })
}
